<template>
  <div class="mark-up-container">
    <!-- ================= 1. 列表页 ================= -->
    <div v-if="currentView === 'list'" class="list-view" ref="listView">
      <!-- 搜索区域 -->
      <div class="search-bar card-box" ref="searchBar">
        <el-form :inline="true" :model="searchForm" size="small" class="search-form">
          <el-form-item label="调价单号">
            <el-input v-model="searchForm.orderNo" placeholder="调价单号" clearable style="width: 160px;"></el-input>
          </el-form-item>
          <el-form-item label="药品名">
            <el-input v-model="searchForm.drugName" placeholder="药品" clearable style="width: 100px;"></el-input>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="searchForm.status" placeholder="全部" clearable style="width: 80px;">
              <el-option label="全部" value=""></el-option>
              <el-option label="草稿" value="1"></el-option>
              <el-option label="已过账" value="2"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="日期起">
            <el-date-picker 
              v-model="searchForm.startDate" 
              type="datetime" 
              placeholder="年/月/日 时:分:秒" 
              value-format="yyyy-MM-dd HH:mm:ss" 
              :default-time="'00:00:00'"
              style="width: 200px;"
              @change="handleStartDateChange"
            ></el-date-picker>
          </el-form-item>
          <el-form-item label="日期止">
            <el-date-picker 
              v-model="searchForm.endDate" 
              type="datetime" 
              placeholder="年/月/日 时:分:秒" 
              value-format="yyyy-MM-dd HH:mm:ss" 
              :default-time="'23:59:59'"
              style="width: 200px;"
              @change="handleEndDateChange"
            ></el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-Ssearch" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 表格区域 -->
      <div class="table-area card-box">
        <div class="table-toolbar" ref="tableToolbar">
          <div class="left-text">
            共 <span class="highlight">{{ total }}</span> 条 调价目录 <span class="highlight">{{ drugCount }}</span> 个药品
          </div>
          <div class="right-actions">
            <el-button size="small" icon="el-icon-download">导出</el-button>
            <el-button size="small" type="primary" icon="el-icon-plus" @click="openForm('add')">新增售价调整单</el-button>
          </div>
        </div>

        <div class="table-scroll-container" v-loading="listLoading">
          <el-table :data="tableData" :height="tableHeight" style="width: 100%" size="small">
            <el-table-column prop="adjustNum" label="调价单号" width="180"></el-table-column>
            <el-table-column prop="adjustDate" label="单据日期" width="150"></el-table-column>
            <el-table-column prop="adjustTypeName" label="调价方式" min-width="150"></el-table-column>
            <el-table-column prop="createName" label="操作人" width="120"></el-table-column>
            <el-table-column prop="state" label="状态" width="100">
              <template slot-scope="scope">
                <!-- state: 0删除 1草稿 2过账 -->
                <el-tag 
                  :type="getStateTagType(scope.row.state)" 
                  size="small" 
                  :effect="scope.row.state === 2 ? 'light' : 'plain'"
                >
                  {{ getStateText(scope.row.state) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" align="center">
              <template slot-scope="scope">
                <i class="el-icon-toLeft action-icon" @click="openDetail(scope.row)"></i>
                <template v-if="scope.row.state === 1">
                  <i class="el-icon-edit action-icon" @click="openForm('edit', scope.row)"></i>
                  <i class="el-icon-delete action-icon delete-icon" @click="handleDelete(scope.row)"></i>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination-wrap" ref="paginationWrap">
          <el-pagination
            background
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total">
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- ================= 2. 新增/编辑/详情 统一弹窗 ================= -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="80%"
      :close-on-click-modal="false"
      custom-class="addxyk-dialog"
      top="5vh"
    >
      <div class="form-view" v-loading="saving || detailLoading">
        <!-- ====== 只读详情模式 ====== -->
        <div v-if="dialogMode === 'view'" class="detail-view-content">
          <el-row :gutter="20" class="basic-info-row">
            <el-col :span="8">
              <div class="info-item"><span class="label">调价单号</span><span class="value">{{ formData.orderNo }}</span></div>
            </el-col>
            <el-col :span="8">
              <div class="info-item"><span class="label">单据日期</span><span class="value">{{ formData.date }}</span></div>
            </el-col>
            <el-col :span="8">
              <div class="info-item"><span class="label">操作人</span><span class="value">{{ formData.operator }}</span></div>
            </el-col>
            <el-col :span="8">
              <div class="info-item"><span class="label">调价方式</span><span class="value">{{ formData.adjustTypeName }}</span></div>
            </el-col>
            <el-col :span="8">
              <div class="info-item"><span class="label">{{ isPercentType ? '加价比例 (%)' : '加价金额 (元)' }}</span><span class="value">{{ formData.percent }}</span></div>
            </el-col>
            <el-col :span="8">
              <div class="info-item"><span class="label">备注</span><span class="value">{{ formData.remark || '—' }}</span></div>
            </el-col>
          </el-row>
        </div>

        <!-- ====== 编辑/新增表单模式 ====== -->
        <el-form v-else :model="formData" label-position="top" size="small">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="调价单号">
                <el-input v-model="formData.orderNo" disabled placeholder="（保存时自动生成）"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="单据日期" required>
                <el-date-picker v-model="formData.date" type="date" placeholder="选择日期" value-format="yyyy-MM-dd" style="width: 100%;"></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="操作人">
                <el-input v-model="formData.operator" disabled placeholder="自动获取中..."></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="调价方式" required>
                <el-select v-model="formData.adjustType" placeholder="请选择调价方式" style="width: 100%;" @change="handleAdjustTypeChange">
                  <el-option 
                    v-for="item in adjustTypeOptions" 
                    :key="item.value" 
                    :label="item.label" 
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="isPercentType ? '加价比例 (%)' : '加价金额 (元)'" required>
                <el-input v-model="formData.percent" :placeholder="isPercentType ? '如 30 表示调增 30%' : '输入调整金额'"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="备注">
                <el-input v-model="formData.remark" placeholder="选填"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <div class="tip-box">
            请选择调价方式，可基于「成本价」加成，或基于「现有售价」调增（比例 / 固定金额）。
          </div>

          <div class="detail-table-title">
            调价明细
            <span class="sub-tip">一行录完按回车，在下面新增一行空白行</span>
          </div>
        </el-form>

        <!-- ====== 明细表格（查看/编辑通用） ====== -->
        <el-table :data="formData.tableData" style="width: 100%; margin-top: 10px;" size="small" border>
          <el-table-column label="序号" width="60" align="center">
            <template slot-scope="scope">{{ scope.$index + 1 }}</template>
          </el-table-column>

          <!-- 药品名称 -->
          <el-table-column width="220" align="center">
            <template slot="header">
              <span style="color:red;">*</span> 药品名称
            </template>
            <template slot-scope="scope">
              <!-- 详情模式显示文本 -->
              <span v-if="dialogMode === 'view'">{{ scope.row.drugName }}</span>
              <!-- 编辑模式显示下拉框 -->
              <div v-else class="drug-select-container" :key="scope.row.id || scope.$index">
                <el-select
                  v-model="scope.row.drugId"
                  filterable
                  placeholder="请选择药品"
                  size="mini"
                  style="width:100%"
                  @change="handleDrugChange(scope.row)"
                  clearable
                  :clearable="true"
                  @clear="handleDrugClear(scope.row)"
                  remote
                  :remote-method="(query) => handleDrugSearch(query, scope.row)"
                  :remote="true"
                  :loading="scope.row.drugLoading"
                  @focus="handleDrugFocus(scope.row)"
                  v-el-select-infinite-scroll="() => handleDrugScroll(scope.row)"
                  popper-class="drug-select-popper"
                >
                  <el-option
                    v-for="drug in getDrugOptionsForRow(scope.row)"
                    :key="drug.drugId"
                    :label="drug.drugName"
                    :value="drug.drugId"
                  >
                    <div class="drug-option">
                      <div style="display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                        <el-tag size="mini" :type="getDrugTypeTagType(drug.drugType)" style="flex-shrink:0;">
                          {{ getDrugTypeText(drug.drugType) }}
                        </el-tag>
                        <span class="drug-name" v-html="getHighlightedDrugName(drug, scope.row)"></span>
                      </div>
                      <template v-if="drug.drugSpecifications || drug.pacSpec || scope.row.spec">
                        <span class="drug-spec">规格：{{ drug.drugSpecifications || drug.pacSpec || scope.row.spec || '' }}</span>
                      </template>
                      <template v-if="drug.manufacturerName || scope.row.manufacturer">
                        <span class="drug-spec">生产厂家：{{ drug.manufacturerName || scope.row.manufacturer || '' }}</span>
                      </template>
                    </div>
                  </el-option>
                  <div v-if="showNoSearchResults(scope.row)" slot="empty" class="no-search-results">
                    <i class="el-icon-search"></i>
                    <span>未找到"{{ scope.row ? scope.row.drugSearchQuery : this.drugSearchQuery }}"相关药品</span>
                  </div>
                  <el-option
                    v-if="drugHasMore && !drugSearchLoading && canLoadMore(scope.row)"
                    :key="`load-more-${scope.row.id}-${Date.now()}`"
                    value="load-more"
                    label="点击加载更多"
                    @click.native="loadMoreDrugs(scope.row)"
                  >
                    <div class="load-more-option" @click.stop="loadMoreDrugs(scope.row)">
                      <i class="el-icon-loading" v-if="loadingMoreDrugs"></i>
                      <span v-else>点击加载更多</span>
                    </div>
                  </el-option>
                </el-select>
              </div>
            </template>
          </el-table-column>

          <!-- 换算关系 -->
          <el-table-column label="换算关系" width="150" align="center">
            <template slot-scope="scope">{{ formData.adjustTypeName || '—' }}</template>
          </el-table-column>

          <!-- 当前最小单位成本价 -->
          <el-table-column label="当前最小单位成本价" width="130" align="right">
            <template slot-scope="scope">
              {{ scope.row.minPurcPric ? '¥' + Number(scope.row.minPurcPric).toFixed(2) : '—' }}
            </template>
          </el-table-column>

          <!-- 当前包装单位成本价 -->
          <el-table-column label="当前包装单位成本价" width="130" align="right">
            <template slot-scope="scope">
              {{ scope.row.pacPurcPric ? '¥' + Number(scope.row.pacPurcPric).toFixed(2) : '—' }}
            </template>
          </el-table-column>

          <el-table-column label="调整项 (勾选回写)" width="140">
            <template slot-scope="scope">
              <el-checkbox v-model="scope.row.adjustPkg" :disabled="dialogMode === 'view'">包装售价</el-checkbox>
              <el-checkbox v-model="scope.row.adjustMin" :disabled="dialogMode === 'view'">最小售价</el-checkbox>
            </template>
          </el-table-column>
          <el-table-column label="当前包装售价" width="110" align="right">
            <template slot-scope="scope">{{ scope.row.currentPkgPrice ? '¥' + scope.row.currentPkgPrice : '—' }}</template>
          </el-table-column>
          <el-table-column label="当前最小售价" width="110" align="right">
            <template slot-scope="scope">{{ scope.row.currentMinPrice ? '¥' + scope.row.currentMinPrice : '—' }}</template>
          </el-table-column>
          <el-table-column label="新包装售价" width="120" align="center">
            <template slot-scope="scope">
              <el-input v-model="scope.row.newPkgPrice" :disabled="dialogMode === 'view' || !scope.row.adjustPkg" size="small"></el-input>
            </template>
          </el-table-column>
          <el-table-column label="新最小售价" width="120" align="center">
            <template slot-scope="scope">
              <el-input v-model="scope.row.newMinPrice" :disabled="dialogMode === 'view' || !scope.row.adjustMin" size="small"></el-input>
            </template>
          </el-table-column>
          <el-table-column label="备注" min-width="120">
            <template slot-scope="scope">
              <el-input v-model="scope.row.remark" :disabled="dialogMode === 'view'" placeholder="选填" size="small"></el-input>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="60" align="center" v-if="dialogMode !== 'view'">
            <template slot-scope="scope">
              <i class="el-icon-delete action-icon" @click="deleteFormRow(scope.$index)"></i>
            </template>
          </el-table-column>
        </el-table>
        
        <div class="add-row-btn" @click="addFormRow" v-if="dialogMode !== 'view'">
          <i class="el-icon-plus"></i> 添加一行
        </div>
      </div>

      <!-- 弹窗底部按钮 -->
		<span slot="footer" class="dialog-footer">
		  <!-- 详情模式：显示关闭；仅草稿状态（state=1）才显示编辑按钮 -->
		  <template v-if="dialogMode === 'view'">
			<el-button size="small" @click="dialogVisible = false">关 闭</el-button>
			<el-button 
			  v-if="currentDetailState === 1" 
			  size="small" 
			  type="primary" 
			  icon="el-icon-edit" 
			  @click="switchToEdit"
			>编 辑</el-button>
		  </template>
		  <!-- 编辑/新增模式：显示取消、保存草稿、过账 -->
		  <template v-else>
			<el-button size="small" @click="dialogVisible = false">取 消</el-button>
			<el-button size="small" icon="el-icon-document" :loading="saving" @click="handleSave(1)">保存草稿</el-button>
			<el-button size="small" type="primary" icon="el-icon-check" :loading="saving" @click="handleSave(2)">过账</el-button>
		  </template>
		</span>
    </el-dialog>
  </div>
</template>

<script>
// 引入接口
import { getStaff } from "@/api/user.js";
import { drugAdjustType, getAllWare, drugSelectAdjust, addAdjust, editAdjust, drugAdjustpageList, deleteAdjust, adjustDetails } from "@/api/PSI.js";

// 自定义指令（无限滚动）
const elSelectInfiniteScroll = {
  bind(el, binding) {
    const SELECTWRAPPER_DOM = el.querySelector('.el-select-dropdown .el-select-dropdown__wrap');
    if (SELECTWRAPPER_DOM) {
      SELECTWRAPPER_DOM.addEventListener('scroll', function() {
        const condition = this.scrollHeight - this.scrollTop - 1 <= this.clientHeight;
        if (condition) {
          binding.value();
        }
      });
    }
  }
};

export default {
  name: 'MarkUp',
  directives: {
    'el-select-infinite-scroll': elSelectInfiniteScroll
  },
  data() {
    return {
      currentView: 'list', // list
      dialogVisible: false, 
	  // 记录当前查看详情的单据状态（1草稿 2过账）
	  currentDetailState: null,
      dialogMode: 'add', // 'add' | 'edit' | 'view'
      tableHeight: 400,
      searchTimer: null,
      currentSearchKeyword: '',
      saving: false,
      detailLoading: false,
      listLoading: false, 

      // 列表页数据
      searchForm: {
        orderNo: '',
        drugName: '',
        status: '',
        startDate: '',
        endDate: ''
      },
      total: 0,
      drugCount: 0,
      currentPage: 1,
      pageSize: 10,
      tableData: [],

      // 表单页数据（统一用于新增、编辑、详情）
      formData: {
        id: null,
        orderNo: '',
        date: new Date().toISOString().split('T')[0],
        operator: '', 
        adjustType: '',
        adjustTypeName: '',
        percent: '',
        remark: '',
        tableData: []
      },

      // 下拉框选项数据
      adjustTypeOptions: [],
      
      // 药品搜索相关
      drugOptions: [],
      drugPageNum: 1,
      drugPageSize: 50,
      drugHasMore: true,
      drugSearchQuery: '',
      drugSearchLoading: false,
      loadingMoreDrugs: false
    };
  },
  computed: {
    validDetailCount() {
      return this.formData.tableData.filter(row => row.drugId).length;
    },
    isPercentType() {
      // 1: 成本价+固定比例(%)  3: 现有售价+固定比例(%)
      return [1, 3].includes(this.formData.adjustType);
    },
    dialogTitle() {
      if (this.dialogMode === 'add') return '新增售价调整单';
      if (this.dialogMode === 'edit') return '编辑售价调整单';
      return '售价调整单详情';
    }
  },
  watch: {
    'formData.adjustType'() {
      if (this.dialogMode !== 'view') this.calculateAllRowPrices();
    },
    'formData.percent'() {
      if (this.dialogMode !== 'view') this.calculateAllRowPrices();
    }
  },
  mounted() {
    this.calcTableHeight();
    window.addEventListener('resize', this.calcTableHeight);
    this.fetchAdjustTypes();
    this.fetchDrugList();
    this.getStaffInfo();
    this.fetchListData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    // ================== 状态显示辅助方法 ==================
    // state: 0删除 1草稿 2过账
    getStateText(state) {
      const stateMap = {
        0: '已删除',
        1: '草稿',
        2: '已过账'
      };
      return stateMap[state] || '未知';
    },
    getStateTagType(state) {
      // el-tag type: success / info / warning / danger
      const typeMap = {
        0: 'danger',   // 已删除 → 红色
        1: 'info',     // 草稿 → 灰色
        2: 'success'   // 已过账 → 绿色
      };
      return typeMap[state] || 'info';
    },

    // ================== 日期时间处理辅助方法 ==================
    /**
     * 开始日期改变处理（双重保障）
     * 由于使用了 default-time="'00:00:00'" 属性，
     * 用户即使只选择日期未点击确定，v-model 值也会自动为 yyyy-MM-dd 00:00:00
     * 此方法仅作兜底格式化
     */
    handleStartDateChange(val) {
      if (!val) return;
      const date = new Date(val.replace(/-/g, '/'));
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      this.$nextTick(() => {
        this.searchForm.startDate = `${y}-${m}-${d} ${h}:${mi}:${s}`;
      });
    },

    /**
     * 结束日期改变处理（双重保障）
     * 由于使用了 default-time="'23:59:59'" 属性，
     * 用户即使只选择日期未点击确定，v-model 值也会自动为 yyyy-MM-dd 23:59:59
     * 此方法仅作兜底格式化，处理用户手动修改时间的场景
     */
    handleEndDateChange(val) {
      if (!val) return;
      const date = new Date(val.replace(/-/g, '/'));
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      this.$nextTick(() => {
        this.searchForm.endDate = `${y}-${m}-${d} ${h}:${mi}:${s}`;
      });
    },

    // ================== 接口请求方法 ==================
    // 获取列表数据
    async fetchListData() {
      this.listLoading = true;
      try {
        const params = {
          adjustNum: this.searchForm.orderNo || '',
          drugName: this.searchForm.drugName || '',
          startTime: this.searchForm.startDate || '',
          endTime: this.searchForm.endDate || '',
          pageNum: this.currentPage,
          pageSize: this.pageSize
        };
        
        if (this.searchForm.status === '1' || this.searchForm.status === 'draft') {
          params.state = 1;
        } else if (this.searchForm.status === '2' || this.searchForm.status === 'posted') {
          params.state = 2;
        }

        const res = await drugAdjustpageList(params);
        if (res && (res.code === 200)) {
          const data = res.data || {};
          this.tableData = data.content || data.list || [];
          this.total = data.totalSize || data.total || 0;
          this.drugCount = data.drugCount || this.tableData.length;
        } else {
          this.$message.error(res.message || '获取列表失败');
        }
      } catch (error) {
        console.error('获取列表失败:', error);
        this.$message.error('获取列表异常');
      } finally {
        this.listLoading = false;
        this.calcTableHeight();
      }
    },

    // 获取详情数据
    async fetchDetailData(id) {
      this.detailLoading = true;
      try {
        const res = await adjustDetails({ id });
        if (res && (res.code === 200 || res.code === 0)) {
          const data = res.data;
          
          // 基础信息映射
          this.formData.id = data.id;
          this.formData.orderNo = data.adjustNum;
          this.formData.date = data.adjustDate;
          this.formData.operator = data.createName || data.operatorName;
          this.formData.adjustType = data.adjustType;
          this.formData.adjustTypeName = data.adjustTypeName;
          this.formData.remark = data.remark || '';
          
          // 根据类型赋值比例或金额
          if ([1, 3].includes(data.adjustType)) {
            this.formData.percent = data.adjustRate;
          } else {
            this.formData.percent = data.adjustPric;
          }

          // 明细映射
          if (data.list && data.list.length > 0) {
            this.formData.tableData = data.list.map(item => {
              // 构造回显需要的药品数据
              const drugObj = {
                drugId: item.drugId,
                drugName: item.drugName,
                drugType: item.drugType,
                drugSpecifications: item.drugSpec,
                pacSpec: item.pacSpec,
                manufacturerName: item.prodentpName,
                pacCnt: item.pacCnt,
                dosUnt: item.dosUnt,
                pacUnt: item.pacUnt
              };
              
              // 确保药品存在于下拉列表中，解决显示ID问题
              const existIndex = this.drugOptions.findIndex(d => d.drugId === item.drugId);
              if (existIndex === -1) {
                this.drugOptions.push(drugObj);
              }

              return {
                id: item.id,
                drugId: item.drugId,
                drugName: item.drugName,
                drugType: item.drugType,
                spec: item.drugSpec,
                adjustPkg: item.isAdjustPacPric === 1,
                adjustMin: item.isAdjustMinPric === 1,
                currentCost: Number(item.pacPurcPric || 0).toFixed(2),
                currentPkgPrice: Number(item.agoFixPacSalePric || 0).toFixed(2),
                currentMinPrice: Number(item.agoFixMinSalePric || 0).toFixed(2),
                newPkgPrice: Number(item.newFixPacSalePric || 0).toFixed(2),
                newMinPrice: Number(item.newFixMinSalePric || 0).toFixed(2),
                pacPurcPric: Number(item.pacPurcPric || 0),
                minPurcPric: Number(item.minPurcPric || 0),
                remark: item.remark || '',
                drugLoading: false,
                drugSearchQuery: '',
                drugSearchResults: []
              };
            });
          } else {
            this.formData.tableData = [];
          }
        } else {
          this.$message.error(res.message || '获取详情失败');
        }
      } catch (error) {
        console.error('获取详情失败:', error);
        this.$message.error('获取详情异常');
      } finally {
        this.detailLoading = false;
      }
    },

    // 删除调价单
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该调价单吗？删除后不可恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        this.listLoading = true;
        const res = await deleteAdjust({ id: row.id });
        if (res && (res.code === 200 || res.code === 0)) {
          this.$message.success('删除成功！');
          if (this.tableData.length === 1 && this.currentPage > 1) {
            this.currentPage--;
          }
          this.fetchListData();
        } else {
          this.$message.error(res.message || '删除失败');
          this.listLoading = false;
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除操作异常');
          this.listLoading = false;
        }
      }
    },

    async getStaffInfo() {
      const userStr = localStorage.getItem('userInfo');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const id = user.staffId;
          if (id) this.fetchOperator(id);
        } catch (e) {
          console.error('解析userInfo失败', e);
        }
      }
    },
    
    async fetchOperator(id) {
      try {
        const res = await getStaff({ id });
        if (res && res.data) {
          this.formData.operator = res.data?.fullName || res.data?.name || '系统管理员';
        }
      } catch (error) {
        console.error('获取操作人信息失败:', error);
      }
    },

    async fetchAdjustTypes() {
      try {
        const res = await drugAdjustType();
        if (res && (res.code === 200 || res.code === 0)) {
          this.adjustTypeOptions = (res.data || []).map(item => ({
            value: item.adjustType,
            label: item.adjustTypeName
          }));
        }
      } catch (error) {
        console.error('获取调价方式失败:', error);
      }
    },

    async fetchDrugList(query = '') {
      try {
        this.drugSearchLoading = true;
        const params = {
          pageNum: this.drugPageNum,
          pageSize: this.drugPageSize,
          drugName: query || ''
        };
        const res = await getAllWare(params);
        if (res && (res.code === 200 || res.code === 0)) {
          const list = res.data?.content || res.data || [];
          const mappedDrugs = list.map(item => ({
            ...item,
            drugId: item.drugId || item.id,
          }));
          
          if (this.drugPageNum === 1) {
            this.drugOptions = mappedDrugs;
          } else {
            const existingIds = new Set(this.drugOptions.map(d => d.drugId));
            const uniqueNewDrugs = mappedDrugs.filter(d => !existingIds.has(d.drugId));
            this.drugOptions = [...this.drugOptions, ...uniqueNewDrugs];
          }
          this.drugHasMore = list.length >= this.drugPageSize;
        }
      } catch (error) {
        console.error('获取药品列表失败:', error);
      } finally {
        this.drugSearchLoading = false;
        this.loadingMoreDrugs = false;
      }
    },

    // ================== 药品搜索逻辑 ==================
    handleDrugSearch(query, row) {
      this.currentSearchKeyword = query || '';
      row.drugSearchQuery = query || '';
      if (!query || query.trim() === '') {
        row.drugSearchResults = [];
        this.drugPageNum = 1;
        this.fetchDrugList();
        return;
      }
      this.drugPageNum = 1;
      row.drugLoading = true;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(async () => {
        try {
          const params = { pageNum: 1, pageSize: this.drugPageSize, drugName: query.trim() };
          const res = await getAllWare(params);
          if (res && (res.code === 200 || res.code === 0)) {
            const list = res.data?.content || res.data || [];
            row.drugSearchResults = list.map(item => ({
              ...item,
              drugId: item.drugId || item.id,
            }));
            this.drugHasMore = list.length >= this.drugPageSize;
          }
        } catch (error) {
          console.error(error);
        } finally {
          row.drugLoading = false;
        }
      }, 300);
    },

    handleDrugFocus(row) {
      if (row.drugSearchQuery && row.drugSearchQuery.trim()) {
        row.drugSearchQuery = '';
        row.drugSearchResults = [];
        this.drugPageNum = 1;
        this.fetchDrugList();
      }
    },

    handleDrugScroll(row) {
      if (this.drugHasMore && !this.drugSearchLoading && !this.loadingMoreDrugs) {
        this.loadMoreDrugs(row);
      }
    },

    loadMoreDrugs(row) {
      if (this.drugHasMore && !this.drugSearchLoading && !this.loadingMoreDrugs) {
        this.loadingMoreDrugs = true;
        this.drugPageNum++;
        this.fetchDrugList(row.drugSearchQuery || '');
      }
    },

    handleDrugClear(row) {
      this.resetRowDrugInfo(row);
      row.drugSearchQuery = '';
      row.drugSearchResults = [];
      this.drugPageNum = 1;
      this.fetchDrugList();
    },

    getDrugOptionsForRow(row) {
      // 优先使用搜索结果，如果没有则使用全局选项
      const opts = (row.drugSearchQuery && row.drugSearchQuery.trim()) 
                   ? (row.drugSearchResults || []) 
                   : (this.drugOptions || []);
      
      // 确保当前选中的值在列表中，否则补全显示
      if (row.drugId && row.drugName) {
        const exists = opts.find(item => item.drugId === row.drugId);
        if (!exists) {
          opts.unshift({
            drugId: row.drugId,
            drugName: row.drugName,
            drugType: row.drugType,
            drugSpecifications: row.spec,
            pacPurcPric: row.pacPurcPric,
            minPurcPric: row.minPurcPric
          });
        }
      }
      return opts;
    },

    getDrugDisplayLabel(drug) {
      return drug.drugName || drug.label || '未知药品';
    },

    getHighlightedDrugName(drug) {
      const drugName = this.getDrugDisplayLabel(drug) || '';
      if (this.currentSearchKeyword && this.currentSearchKeyword.trim()) {
        const regex = new RegExp(`(${this.currentSearchKeyword.trim()})`, 'gi');
        return drugName.replace(regex, '<span class="highlight-keyword">$1</span>');
      }
      return drugName;
    },

    showNoSearchResults(row) {
      const hasSearchQuery = row.drugSearchQuery && row.drugSearchQuery.trim();
      const hasSearchResults = row.drugSearchResults && row.drugSearchResults.length > 0;
      return hasSearchQuery && !hasSearchResults;
    },

    canLoadMore() {
      return this.drugHasMore && !this.drugSearchLoading && !this.loadingMoreDrugs;
    },

    getDrugTypeText(type) {
      const map = { 1: '西药', 2: '中药', 3: '卫材' };
      return map[type] || '未知';
    },
    getDrugTypeTagType(type) {
      const map = { 1: 'success', 2: 'warning', 3: 'danger' };
      return map[type] || 'info';
    },

    // ================== 药品选择与数据回填 ==================
    async handleDrugChange(row) {
      if (!row.drugId) {
        this.resetRowDrugInfo(row);
        return;
      }

      // 1. 防重校验
      const isDuplicate = this.formData.tableData.some(
        item => item !== row && item.drugId === row.drugId
      );
      if (isDuplicate) {
        this.$message.warning('该药品已在明细中，请勿重复添加！');
        row.drugId = '';
        this.resetRowDrugInfo(row);
        return;
      }

      // 2. 获取选中的药品
      const selectedDrug = this.drugOptions.find(item => item.drugId === row.drugId) || 
                           (row.drugSearchResults || []).find(item => item.drugId === row.drugId);
      if (!selectedDrug) return;

      row.drugType = selectedDrug.drugType || 1;
      row.drugName = selectedDrug.drugName;
      row.spec = selectedDrug.drugSpecifications || selectedDrug.drugSpec || '';

      // 3. 调用 drugSelectAdjust 接口
      try {
        this.$set(row, 'loading', true);
        const params = { drugId: row.drugId, drugType: row.drugType };
        const res = await drugSelectAdjust(params);
        
        if (res && (res.code === 200 || res.code === 0) && res.data) {
          const data = res.data; 
          
          row.pacPurcPric = data.pacPurcPric || 0; 
          row.minPurcPric = data.minPurcPric || 0; 
          row.currentCost = row.pacPurcPric.toFixed(2); 
          
          row.currentPkgPrice = (data.agoFixPacSalePric || 0).toFixed(2); 
          row.currentMinPrice = (data.agoFixMinSalePric || 0).toFixed(2); 
          
          row.newPkgPrice = row.currentPkgPrice;
          row.newMinPrice = row.currentMinPrice;

          this.calculateRowPrice(row);
        }
      } catch (error) {
        console.error('获取药品调价信息失败:', error);
        this.$message.error('获取药品调价信息失败');
      } finally {
        this.$set(row, 'loading', false);
      }
    },

    resetRowDrugInfo(row) {
      row.drugName = '';
      row.spec = '';
      row.currentCost = '';
      row.currentPkgPrice = '';
      row.currentMinPrice = '';
      row.newPkgPrice = '';
      row.newMinPrice = '';
      row.adjustPkg = false;
      row.adjustMin = false;
      row.pacPurcPric = 0;
      row.minPurcPric = 0;
    },

    // ================== 价格计算逻辑 ==================
    handleAdjustTypeChange(val) {
      const target = this.adjustTypeOptions.find(item => item.value === val);
      this.formData.adjustTypeName = target ? target.label : '';
    },

    calculateAllRowPrices() {
      this.formData.tableData.forEach(row => {
        if (row.drugId) {
          this.calculateRowPrice(row);
        }
      });
    },

    calculateRowPrice(row) {
      const type = this.formData.adjustType;
      const percent = parseFloat(this.formData.percent) || 0;
      if (!row.drugId || !type) return;

      let basePkg = 0;
      let baseMin = 0;

      if (type === 1 || type === 2) {
        basePkg = row.pacPurcPric || 0;
        baseMin = row.minPurcPric || 0;
      } else if (type === 3 || type === 4) {
        basePkg = parseFloat(row.currentPkgPrice) || 0;
        baseMin = parseFloat(row.currentMinPrice) || 0;
      }

      if (type === 1 || type === 3) {
        row.newPkgPrice = (basePkg * (1 + percent / 100)).toFixed(2);
        row.newMinPrice = (baseMin * (1 + percent / 100)).toFixed(2);
      } else if (type === 2 || type === 4) {
        row.newPkgPrice = (basePkg + percent).toFixed(2);
        row.newMinPrice = (baseMin + percent).toFixed(2);
      }
    },

    // ================== 列表与页面切换 ==================
    calcTableHeight() {
      this.$nextTick(() => {
        if (this.currentView !== 'list') return;
        const listView = this.$refs.listView;
        if (!listView) return;
        
        const searchBarHeight = this.$refs.searchBar ? this.$refs.searchBar.offsetHeight : 0;
        const toolbarHeight = this.$refs.tableToolbar ? this.$refs.tableToolbar.offsetHeight : 0;
        const paginationHeight = this.$refs.paginationWrap ? this.$refs.paginationWrap.offsetHeight : 0;

        const listViewHeight = listView.clientHeight; 
        let availableHeight = listViewHeight - searchBarHeight - toolbarHeight - paginationHeight - 60;
        this.tableHeight = Math.max(availableHeight, 200);
      });
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchListData();
    },
    resetSearch() {
      this.searchForm = { orderNo: '', drugName: '', status: '', startDate: '', endDate: '' };
      this.handleSearch();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchListData();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchListData();
    },

    // 打开详情弹窗
	openDetail(row) {
	  this.dialogMode = 'view';
	  this.dialogVisible = true;
	  // 记录当前单据的状态，用于控制是否显示编辑按钮
	  this.currentDetailState = row.state;
	  this.fetchDetailData(row.id);
	},

    // 打开新增/编辑弹窗
    openForm(type, row) {
      this.dialogMode = type;
      this.dialogVisible = true;

      if (type === 'add') {
        this.formData = {
          id: null,
          orderNo: '',
          date: new Date().toISOString().split('T')[0],
          operator: this.formData.operator || '',
          adjustType: '',
          adjustTypeName: '',
          percent: '',
          remark: '',
          tableData: [this.createEmptyRow()]
        };
      } else if (type === 'edit') {
        // 编辑时调详情接口获取最新数据
        this.fetchDetailData(row.id);
      }
    },

    // 详情转编辑
    switchToEdit() {
      this.dialogMode = 'edit';
      // 如果需要在切换时重新计算或补全表单，可在此处处理
    },

    createEmptyRow() {
      return {
        drugId: '',
        drugName: '',
        spec: '',
        drugLoading: false,
        drugSearchQuery: '',
        drugSearchResults: [],
        adjustPkg: false,
        adjustMin: false,
        currentCost: '',
        currentPkgPrice: '',
        currentMinPrice: '',
        newPkgPrice: '',
        newMinPrice: '',
        pacPurcPric: 0,
        minPurcPric: 0,
        remark: ''
      };
    },
    addFormRow() {
      this.formData.tableData.push(this.createEmptyRow());
    },
    deleteFormRow(index) {
      if (this.formData.tableData.length > 1) {
        this.formData.tableData.splice(index, 1);
      } else {
        this.$message.warning('至少保留一行明细');
      }
    },

    // ================== 保存与过账逻辑 ==================
    async handleSave(state) {
      if (!this.formData.adjustType) {
        this.$message.warning('请选择调价方式！');
        return;
      }
      if (!this.formData.percent) {
        this.$message.warning(this.isPercentType ? '请输入加价比例！' : '请输入加价金额！');
        return;
      }

      const validDetails = this.formData.tableData.filter(row => row.drugId);
      if (validDetails.length === 0) {
        this.$message.warning('请至少添加一条有效的药品调价明细！');
        return;
      }

      for (let i = 0; i < validDetails.length; i++) {
        const row = validDetails[i];
        if (!row.adjustPkg && !row.adjustMin) {
          this.$message.warning(`第 ${i + 1} 行药品「${row.drugName}」请至少勾选一个调整项！`);
          return;
        }
      }

      // 3. 组装明细参数
      const list = validDetails.map(row => {
        return {
          drugId: row.drugId,
          drugType: row.drugType,
          agoFixPacSalePric: Number(row.currentPkgPrice) || 0,
          agoFixMinSalePric: Number(row.currentMinPrice) || 0,
          newFixPacSalePric: Number(row.newPkgPrice) || 0,
          newFixMinSalePric: Number(row.newMinPrice) || 0,
          pacPurcPric: Number(row.pacPurcPric) || 0,
          minPurcPric: Number(row.minPurcPric) || 0,
          isAdjustPacPric: row.adjustPkg ? 1 : 0,
          isAdjustMinPric: row.adjustMin ? 1 : 0,
          remark: row.remark || ''
        };
      });

      // 4. 组装最终参数
      const params = {
        id: this.formData.id,
        adjustDate: this.formData.date,
        adjustType: this.formData.adjustType,
        list: list,
        state: state,
        remark: this.formData.remark || '',
        adjustPric: this.isPercentType ? 0 : Number(this.formData.percent),
        adjustRate: this.isPercentType ? Number(this.formData.percent) : 0
      };

      try {
        this.saving = true;
        let res;
        
        if (this.dialogMode === 'edit') {
          res = await editAdjust(params);
        } else {
          res = await addAdjust(params);
        }

        if (res && (res.code === 200 || res.code === 0)) {
          this.$message.success(state === 1 ? '保存草稿成功！' : '过账成功！');
          this.dialogVisible = false;
          this.fetchListData();
        } else {
          this.$message.error(res.message || (state === 1 ? '保存草稿失败' : '过账失败'));
        }
      } catch (error) {
        console.error('提交失败:', error);
        this.$message.error('提交失败，请检查网络或参数');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="less">
.mark-up-container {
  padding: 16px;
  background-color: #f5f7fa;
  height: calc(100vh - 100px); 
  box-sizing: border-box;

  .card-box {
    background: #ffffff;
    border-radius: 8px;
    padding: 16px 20px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  }

  .highlight {
    color: #1677ff;
    font-weight: 500;
  }

  .green-text {
    color: #00b42a;
    font-weight: 500;
  }

  .action-icon {
    font-size: 16px;
    cursor: pointer;
    margin: 0 6px;
    color: #606266;
    &:hover {
      color: #1677ff;
    }
    &.delete-icon:hover {
      color: #f56c6c;
    }
  }

  /* ================= 列表页 ================= */
  .list-view {
    height: 100%; 
    display: flex;
    flex-direction: column;

    .search-bar {
      flex-shrink: 0;
      margin-bottom: 16px;
      padding-bottom: 0;
      .search-form {
        .el-form-item {
          margin-bottom: 16px;
        }
      }
    }

    .table-area {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0; 
      margin-bottom: 0;
      padding-bottom: 16px;

      .table-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        flex-shrink: 0; 

        .left-text {
          font-size: 13px;
          color: #606266;
        }
      }

      .table-scroll-container {
        flex: 1;
        overflow: hidden; 
        margin-bottom: 16px;
      }

      .pagination-wrap {
        flex-shrink: 0;
        display: flex;
        justify-content: flex-end;
      }
    }
  }

  /* ================= 表单页 (弹窗内) ================= */
  .form-view {
    .detail-view-content {
      background-color: #fafafa;
      padding: 16px 20px;
      border-radius: 6px;
      margin-bottom: 20px;
      
      .basic-info-row {
        .info-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 16px;
          .label {
            font-size: 12px;
            color: #909399;
            margin-bottom: 4px;
          }
          .value {
            font-size: 14px;
            color: #303133;
            font-weight: 500;
          }
        }
      }
    }

    .tip-box {
      background-color: #fffbe6;
      border: 1px solid #ffe58f;
      color: #faad14;
      font-size: 13px;
      padding: 8px 12px;
      border-radius: 4px;
      margin-bottom: 20px;
    }

    .detail-table-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .sub-tip {
        font-size: 12px;
        color: #909399;
        font-weight: normal;
      }
    }

    .el-checkbox {
      display: block;
      margin-left: 0;
      margin-bottom: 4px;
    }

    .add-row-btn {
      text-align: center;
      padding: 10px 0;
      color: #606266;
      font-size: 13px;
      cursor: pointer;
      border: 1px dashed #dcdfe6;
      border-top: none;
      border-radius: 0 0 4px 4px;
      transition: all 0.2s;

      &:hover {
        color: #1677ff;
        border-color: #1677ff;
      }
    }
  }
}
</style>

<style lang="less" scoped>
::v-deep .el-dialog__header {
	background-color: #2c4f79 !important;
	padding: 12px 20px;
	border-bottom: 1px solid #1f3a57;
	border-radius: 8px 8px 0 0;
}
::v-deep .el-dialog__title {
	color: #ffffff !important;
	font-size: 16px;
	font-weight: 600;
}

::v-deep .el-dialog__headerbtn .el-dialog__close {
	color: #ffffff !important;
	&:hover {
	  color: #e6e6e6 !important;
	}
}
/* 全局弹窗样式 */
::v-deep .addxyk-dialog {
  border-radius: 8px;
  
  .el-dialog__body {
    padding: 20px 30px;
    background-color: #ffffff;
  }

  .el-dialog__footer {
    padding: 10px 20px;
    border-top: 1px solid #ebeef5;
  }
}

/* 药品下拉框样式 */
.drug-select-popper {
  .el-select-dropdown__item {
    height: auto;
    min-height: 50px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    &:hover,
    &.selected,
    &.hover {
      .drug-option .drug-name .highlight-keyword {
        color: #ff6700;
        background-color: rgba(255,103,0,0.15);
      }
    }
    .drug-option {
      display: flex;
      flex-direction: column;
      width: 100%;
      line-height: 1.4;
      min-height: 50px;
      .drug-name {
        font-weight: 600;
        color: #303133;
        margin-bottom: 3px;
        font-size: 13px;
        .highlight-keyword {
          color: #ff6700;
          font-weight: bold;
          background-color: rgba(255,103,0,0.1);
          padding: 0 2px;
          border-radius: 2px;
        }
      }
      .drug-spec {
        font-size: 12px;
        color: #606266;
        line-height: 1.3;
        &:not(:last-child) {
          margin-bottom: 2px;
        }
      }
    }
  }
}
</style>